/**
 * SafetyPanel — versioned, scoped AI safety configuration rendered as a
 * sub-tab inside the AI Agents page. Mirrors the panel conventions used by
 * GuardrailsPanel / OrchestratorsPanel:
 *   - header row with title + primary action
 *   - read-only summary of the effective configuration
 *   - right-side ResourceDrawer for editing (and for version history)
 *   - inline test panel inside the edit drawer
 */
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react'
import {
  ShieldCheck,
  Lock,
  AlertOctagon,
  Pencil,
  History,
  FlaskConical,
  Loader2,
} from 'lucide-react'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import RequirePermission from '../../auth/RequirePermission.js'
import ResourceDrawer from '../ResourceDrawer.js'
import {
  DRAWER_INPUT_CLASS,
  DRAWER_LABEL_CLASS,
  DRAWER_HINT_CLASS,
  DRAWER_SECTION_CLASS,
  ScopeBar,
  InheritingBanner,
  LastModifiedLabel,
  DrawerTabs,
  TestPanel,
  VersionHistoryDrawer,
  type TestTraceResult,
} from './versioned-config-ui.js'
import Select from '../../ui/Select.js'
import OversightSettingsCard from './OversightSettingsCard.js'

// ---------------------------------------------------------------------------
// Types (mirror apps/api/src/routes/admin/ai/safety.ts)
// ---------------------------------------------------------------------------

type SafetyActionOnTrip =
  | 'block_and_log'
  | 'block_and_alert'
  | 'redact_and_continue'
  | 'flag_for_review'

interface SafetyConfigInput {
  piiRedaction: {
    enabled: boolean
    categories: {
      creditCard: boolean
      ssnTaxId: boolean
      personalEmail: boolean
      phoneNumber: boolean
    }
  }
  toxicity: {
    thresholdPercent: number
    actionOnTrip: SafetyActionOnTrip
  }
  hallucinationVerification: {
    strictness: 'low' | 'medium' | 'high'
    actionOnTrip: SafetyActionOnTrip
  }
  forbiddenTopics: {
    keywords: string[]
    actionOnTrip: SafetyActionOnTrip
  }
  fallbackMessage: string
}

interface SafetyConfig extends SafetyConfigInput {
  id: string
  scope: 'global' | 'agent'
  agentId: string | null
  version: number
  isActive: boolean
  createdBy: string
  createdAt: string
}

interface SafetyResponse {
  config: SafetyConfig
  inheriting: boolean
}

interface AgentRow {
  id: string
  slug: string
  name: string
  is_enabled: boolean
}

interface TestResult extends TestTraceResult {
  sanitizedInput: string
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACTION_LABELS: Record<SafetyActionOnTrip, string> = {
  block_and_log: 'Block and log',
  block_and_alert: 'Block and notify admin',
  redact_and_continue: 'Redact and continue',
  flag_for_review: 'Flag for human review',
}

/** Lower-cased action label; tolerates an unknown/missing action so a stale
 *  config row can never take the whole panel down. */
function actionLabel(action: SafetyActionOnTrip | undefined): string {
  return (action && ACTION_LABELS[action] ? ACTION_LABELS[action] : 'Unknown action').toLowerCase()
}

const OUTPUT_ACTIONS: SafetyActionOnTrip[] = [
  'block_and_log',
  'block_and_alert',
  'redact_and_continue',
  'flag_for_review',
]

// `redact_and_continue` is meaningless for a binary keyword hit.
const TOPIC_ACTIONS: SafetyActionOnTrip[] = [
  'block_and_log',
  'block_and_alert',
  'flag_for_review',
]

const PII_CATEGORIES: Array<{
  key: keyof SafetyConfigInput['piiRedaction']['categories']
  label: string
  example: string
}> = [
  { key: 'creditCard', label: 'Credit Card Numbers', example: 'e.g. 4111 •••• •••• 1234' },
  { key: 'ssnTaxId', label: 'Social Security / Tax ID', example: 'e.g. •••-••-6789' },
  { key: 'personalEmail', label: 'Personal Email Addresses', example: 'e.g. j••••@gmail.com' },
  { key: 'phoneNumber', label: 'Phone Numbers', example: 'e.g. (555) •••-4567' },
]

const STRICTNESS_HELP: Record<'low' | 'medium' | 'high', string> = {
  low: 'Only flags confident, specific claims (a date, statistic, citation, or number) that are very unlikely to be verifiable.',
  medium: 'Flags confidently-stated specifics that read as invented rather than verified, even if plausible-sounding.',
  high: 'Flags any specific factual claim stated as fact rather than an estimate, unless it is common general knowledge.',
}

function toInput(config: SafetyConfig): SafetyConfigInput {
  return {
    piiRedaction: config.piiRedaction,
    toxicity: config.toxicity,
    hallucinationVerification: config.hallucinationVerification,
    forbiddenTopics: config.forbiddenTopics,
    fallbackMessage: config.fallbackMessage,
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SafetyPanel(): JSX.Element {
  const toast = useToast()
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [scope, setScope] = useState<'global' | 'agent'>('global')
  const [scopeAgentId, setScopeAgentId] = useState<string | null>(null)

  const [config, setConfig] = useState<SafetyConfig | null>(null)
  const [form, setForm] = useState<SafetyConfigInput | null>(null)
  const [inheriting, setInheriting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [newTopic, setNewTopic] = useState('')
  const [showPiiHelp, setShowPiiHelp] = useState(false)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerTab, setDrawerTab] = useState<'pii' | 'moderation' | 'topics' | 'test'>('pii')
  const [historyOpen, setHistoryOpen] = useState(false)
  const [versions, setVersions] = useState<SafetyConfig[]>([])

  const [testText, setTestText] = useState('')
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<TestResult | null>(null)

  const dirty = useMemo(
    () =>
      config !== null &&
      form !== null &&
      JSON.stringify(form) !== JSON.stringify(toInput(config)),
    [config, form],
  )

  // Load agents once for the scope selector.
  useEffect(() => {
    api
      .get<{ agents: AgentRow[] }>('/admin/ai/agents')
      .then((res) => setAgents(res.agents ?? []))
      .catch(() => setAgents([]))
  }, [])

  const scopeParams = useCallback(
    () =>
      scope === 'agent' && scopeAgentId
        ? `?scope=agent&agentId=${encodeURIComponent(scopeAgentId)}`
        : '?scope=global',
    [scope, scopeAgentId],
  )

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<SafetyResponse>(`/admin/ai/safety${scopeParams()}`)
      setConfig(res.config)
      setForm(toInput(res.config))
      setInheriting(res.inheriting)
      setTestResult(null)
    } catch (err) {
      toast.error('Failed to load safety configuration', String(err))
    } finally {
      setLoading(false)
    }
  }, [scopeParams, toast])

  useEffect(() => {
    void load()
  }, [load])

  async function handleSave(): Promise<void> {
    if (!config || !form || !dirty) return
    setSaving(true)
    try {
      const res = await api.put<SafetyResponse>('/admin/ai/safety', {
        scope,
        agentId: scope === 'agent' ? scopeAgentId : null,
        config: form,
      })
      setConfig(res.config)
      setForm(toInput(res.config))
      setInheriting(false)
      setDrawerOpen(false)
      toast.success(
        'Safety settings saved',
        scope === 'agent'
          ? `Agent override saved as v${res.config.version}.`
          : `Global configuration saved as v${res.config.version}.`,
      )
    } catch (err) {
      toast.error('Failed to save safety settings', String(err))
    } finally {
      setSaving(false)
    }
  }

  async function openHistory(): Promise<void> {
    try {
      const res = await api.get<{ versions: SafetyConfig[] }>(
        `/admin/ai/safety/history${scopeParams()}`,
      )
      setVersions(res.versions)
      setHistoryOpen(true)
    } catch (err) {
      toast.error('Failed to load version history', String(err))
    }
  }

  async function handleRestore(versionId: string): Promise<void> {
    try {
      const res = await api.post<SafetyResponse>('/admin/ai/safety/restore', { versionId })
      setConfig(res.config)
      setForm(toInput(res.config))
      setInheriting(false)
      setHistoryOpen(false)
      toast.success('Version restored', `Rolled back — now active as v${res.config.version}.`)
    } catch (err) {
      toast.error('Failed to restore version', String(err))
    }
  }

  async function runTest(): Promise<void> {
    if (!form || !testText.trim()) return
    setTesting(true)
    setTestResult(null)
    try {
      const res = await api.post<TestResult>('/admin/ai/safety/test', {
        text: testText,
        config: form,
      })
      setTestResult(res)
    } catch (err) {
      toast.error('Test failed', String(err))
    } finally {
      setTesting(false)
    }
  }

  function addBlockedTopic(): void {
    if (!newTopic.trim() || !form) return
    if (form.forbiddenTopics.keywords.includes(newTopic.trim())) return
    setForm({
      ...form,
      forbiddenTopics: {
        ...form.forbiddenTopics,
        keywords: [...form.forbiddenTopics.keywords, newTopic.trim()],
      },
    })
    setNewTopic('')
  }

  function removeBlockedTopic(topic: string): void {
    if (!form) return
    setForm({
      ...form,
      forbiddenTopics: {
        ...form.forbiddenTopics,
        keywords: form.forbiddenTopics.keywords.filter((t) => t !== topic),
      },
    })
  }

  function patch(partial: Partial<SafetyConfigInput>): void {
    if (!form) return
    setForm({ ...form, ...partial })
  }

  if (loading || !config || !form) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading safety settings…
      </div>
    )
  }

  const enabledPii = PII_CATEGORIES.filter((c) => config.piiRedaction.categories[c.key])

  return (
    <div className="space-y-4">
      {/* Header row — matches GuardrailsPanel / OrchestratorsPanel */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Safety</h3>
          <p className="text-xs text-gray-500">
            PII redaction, toxicity ceiling, and forbidden topics enforced in the request pipeline
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            onClick={() => void openHistory()}
          >
            <History className="h-3.5 w-3.5" />
            View History
          </button>
          <RequirePermission permission="admin:extensions:manage" fallback={null}>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0]"
              onClick={() => setDrawerOpen(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit Configuration
            </button>
          </RequirePermission>
        </div>
      </div>

      {/* Scope bar */}
      <ScopeBar
        agents={agents}
        scope={scope}
        scopeAgentId={scopeAgentId}
        onScopeChange={(s, agentId) => {
          setScope(s)
          setScopeAgentId(agentId)
        }}
        inheriting={inheriting}
        trailing={<LastModifiedLabel config={config} />}
      />

      {/* Inherited-state banner */}
      {scope === 'agent' && inheriting && <InheritingBanner />}

      {scope === 'global' && <OversightSettingsCard />}

      {/* Read-only summary */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-4">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-[#3454cf]" />
            <h4 className="text-xs font-semibold text-gray-900">PII & Sensitive Data Redaction</h4>
          </div>
          <p className="mt-2 text-xs text-gray-600">
            {config.piiRedaction.enabled
              ? enabledPii.length > 0
                ? `Enabled — masking ${enabledPii.map((c) => c.label).join(', ')}`
                : 'Enabled — no categories selected'
              : 'Disabled'}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#3454cf]" />
            <h4 className="text-xs font-semibold text-gray-900">Toxicity & Harm Moderation</h4>
          </div>
          <p className="mt-2 text-xs text-gray-600">
            Threshold {config.toxicity.thresholdPercent}% — {actionLabel(config.toxicity.actionOnTrip)}
          </p>
          <p className="mt-1 text-[11px] text-gray-400">
            Hallucination verification: {config.hallucinationVerification.strictness} —{' '}
            {actionLabel(config.hallucinationVerification.actionOnTrip)}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-4">
          <div className="flex items-center gap-2">
            <AlertOctagon className="h-4 w-4 text-[#3454cf]" />
            <h4 className="text-xs font-semibold text-gray-900">Forbidden Topics & Fallback</h4>
          </div>
          <p className="mt-2 text-xs text-gray-600">
            {config.forbiddenTopics.keywords.length} blocked keyword
            {config.forbiddenTopics.keywords.length === 1 ? '' : 's'} —{' '}
            {actionLabel(config.forbiddenTopics.actionOnTrip)}
          </p>
          <p className="mt-1 truncate text-[11px] text-gray-400" title={config.fallbackMessage}>
            “{config.fallbackMessage}”
          </p>
        </div>
      </div>

      {/* ── Edit drawer ── */}
      <ResourceDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSave}
        title={`Safety Configuration — ${scope === 'global' ? 'Global (all agents)' : `Agent: ${scopeAgentId}`}`}
        icon={<ShieldCheck className="h-4 w-4 text-white" />}
        width="xl"
        saving={saving}
        disabled={!dirty}
        saveLabel="Save Settings"
      >
        {/* Tab bar — same style as the tool/agent drawers */}
        <DrawerTabs
          tabs={[
            { key: 'pii' as const, label: 'PII & Data', icon: <Lock className="h-3.5 w-3.5" /> },
            { key: 'moderation' as const, label: 'Moderation', icon: <ShieldCheck className="h-3.5 w-3.5" /> },
            { key: 'topics' as const, label: 'Topics & Fallback', icon: <AlertOctagon className="h-3.5 w-3.5" /> },
            { key: 'test' as const, label: 'Test', icon: <FlaskConical className="h-3.5 w-3.5" /> },
          ]}
          active={drawerTab}
          onChange={setDrawerTab}
        />

        <div className="flex flex-col gap-4">
          {/* PII & Data Privacy */}
          {drawerTab === 'pii' && (
            <div>
              <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={form.piiRedaction.enabled}
                  onChange={(e) =>
                    patch({ piiRedaction: { ...form.piiRedaction, enabled: e.target.checked } })
                  }
                  className="rounded border-gray-300 text-[#3454cf]"
                />
                Mask PII before model ingestion
              </label>

              {form.piiRedaction.enabled && (
                <div className="mt-2 grid grid-cols-2 gap-2 rounded-sm border border-gray-200 bg-gray-50/60 p-3">
                  {PII_CATEGORIES.map(({ key, label, example }) => (
                    <label key={key} className="flex items-start gap-2 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={form.piiRedaction.categories[key]}
                        onChange={(e) =>
                          patch({
                            piiRedaction: {
                              ...form.piiRedaction,
                              categories: {
                                ...form.piiRedaction.categories,
                                [key]: e.target.checked,
                              },
                            },
                          })
                        }
                        className="mt-0.5 rounded border-gray-300"
                      />
                      <span className="flex flex-col">
                        {label}
                        {form.piiRedaction.categories[key] && (
                          <span className="font-mono text-[10px] text-gray-400">{example}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowPiiHelp((v) => !v)}
                className="mt-2 text-[11px] font-medium text-[#3454cf] hover:underline"
              >
                What counts as PII? →
              </button>
              {showPiiHelp && (
                <p className="mt-1 rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] leading-relaxed text-gray-600">
                  Detected by pattern: credit cards, US SSN / Tax IDs, personal emails, phone
                  numbers. Names, addresses, and dates of birth are <em>not</em> detected.
                </p>
              )}
            </div>
          )}

          {/* Toxicity & Harm Filter */}
          {drawerTab === 'moderation' && (
            <div className="flex flex-col gap-4">
              <h4 className={DRAWER_SECTION_CLASS}>Toxicity & Harm Moderation</h4>
              <div>
                <label className={DRAWER_LABEL_CLASS}>
                  Toxicity threshold — {form.toxicity.thresholdPercent}%
                </label>
                <input
                  type="range"
                  min="10"
                  max="95"
                  step="5"
                  value={form.toxicity.thresholdPercent}
                  onChange={(e) =>
                    patch({
                      toxicity: { ...form.toxicity, thresholdPercent: Number(e.target.value) },
                    })
                  }
                  className="w-full"
                />
                <div className="flex justify-between text-[10px] text-gray-400">
                  <span>Strict (10%)</span>
                  <span>Permissive (95%)</span>
                </div>
              </div>

              <div>
                <label className={DRAWER_LABEL_CLASS}>When threshold is exceeded</label>
                <Select
                  ariaLabel="When threshold is exceeded"
                  value={form.toxicity.actionOnTrip}
                  onChange={(v) =>
                    patch({
                      toxicity: {
                        ...form.toxicity,
                        actionOnTrip: v as SafetyActionOnTrip,
                      },
                    })
                  }
                >
                  {OUTPUT_ACTIONS.map((a) => (
                    <option key={a} value={a}>
                      {ACTION_LABELS[a]}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label className={DRAWER_LABEL_CLASS}>Hallucination verification</label>
                <Select
                  ariaLabel="Hallucination verification strictness"
                  value={form.hallucinationVerification.strictness}
                  onChange={(v) =>
                    patch({
                      hallucinationVerification: {
                        ...form.hallucinationVerification,
                        strictness: v as 'low' | 'medium' | 'high',
                      },
                    })
                  }
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </Select>
                <span className={DRAWER_HINT_CLASS}>
                  {STRICTNESS_HELP[form.hallucinationVerification.strictness]}
                </span>
              </div>

              <div>
                <label className={DRAWER_LABEL_CLASS}>On likely fabrication</label>
                <Select
                  ariaLabel="On likely fabrication"
                  value={form.hallucinationVerification.actionOnTrip}
                  onChange={(v) =>
                    patch({
                      hallucinationVerification: {
                        ...form.hallucinationVerification,
                        actionOnTrip: v as SafetyActionOnTrip,
                      },
                    })
                  }
                >
                  {TOPIC_ACTIONS.map((a) => (
                    <option key={a} value={a}>
                      {ACTION_LABELS[a]}
                    </option>
                  ))}
                </Select>
                <span className={DRAWER_HINT_CLASS}>
                  What happens when the response is flagged as a likely fabrication.
                </span>
              </div>
            </div>
          )}

          {/* Forbidden topics + fallback */}
          {drawerTab === 'topics' && (
            <div className="flex flex-col gap-4">
              <div>
                <label className={DRAWER_LABEL_CLASS}>Blocked keywords</label>
                <div className="flex gap-2">
                  <input
                    value={newTopic}
                    onChange={(e) => setNewTopic(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addBlockedTopic()
                    }}
                    placeholder="e.g. internal server IP"
                    className={DRAWER_INPUT_CLASS}
                  />
                  <button
                    type="button"
                    onClick={addBlockedTopic}
                    className="rounded-sm border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Add
                  </button>
                </div>
                <span className={DRAWER_HINT_CLASS}>
                  Exact keyword match only — no semantic detection.
                </span>
                {form.forbiddenTopics.keywords.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {form.forbiddenTopics.keywords.map((topic) => (
                      <span
                        key={topic}
                        className="inline-flex items-center gap-1 rounded-sm bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700"
                      >
                        {topic}
                        <button
                          onClick={() => removeBlockedTopic(topic)}
                          className="hover:text-red-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className={DRAWER_LABEL_CLASS}>When a topic is matched</label>
                <Select
                  ariaLabel="When a topic is matched"
                  value={form.forbiddenTopics.actionOnTrip}
                  onChange={(v) =>
                    patch({
                      forbiddenTopics: {
                        ...form.forbiddenTopics,
                        actionOnTrip: v as SafetyActionOnTrip,
                      },
                    })
                  }
                >
                  {TOPIC_ACTIONS.map((a) => (
                    <option key={a} value={a}>
                      {ACTION_LABELS[a]}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label className={DRAWER_LABEL_CLASS}>Fallback message (shown when blocked)</label>
                <textarea
                  value={form.fallbackMessage}
                  onChange={(e) => patch({ fallbackMessage: e.target.value })}
                  rows={2}
                  className={DRAWER_INPUT_CLASS}
                />
              </div>
            </div>
          )}

          {/* Test panel — runs against the unsaved form state */}
          {drawerTab === 'test' && (
            <TestPanel
              title={
                <>
                  <FlaskConical className="h-3.5 w-3.5" /> Test this configuration
                </>
              }
              onRun={() => void runTest()}
              running={testing}
              runDisabled={!testText.trim()}
              result={testResult}
            >
              <input
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                placeholder="e.g. My SSN is 123-45-6789"
                className={DRAWER_INPUT_CLASS}
              />
            </TestPanel>
          )}
        </div>
      </ResourceDrawer>

      {/* ── Version history drawer ── */}
      <VersionHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={`Version History — ${scope === 'global' ? 'Global' : `Agent: ${scopeAgentId}`}`}
        versions={versions}
        onRestore={(versionId) => void handleRestore(versionId)}
      />
    </div>
  )
}
